import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BananaApi } from "../api/BananaApi";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { MyRelayJourney } from "../relay/RelayTypes";
import { BananaType } from "../types/BananaType";
import { logRelayEvent } from "../analytics/RelayAnalytics";

const reactionSummary = (journey: MyRelayJourney) => [
  ["❤️", journey.reactionCounts.heart || 0],
  ["😊", journey.reactionCounts.smile || 0],
  ["🍌", journey.reactionCounts.banana || 0],
].filter(([, count]) => Number(count) > 0);

const YourBananasPage = () => {
  const [journeys, setJourneys] = useState<MyRelayJourney[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    logRelayEvent("your_bananas_viewed");
    BananaApi.getMyRelayJourneys()
      .then(setJourneys)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  return <main className="min-h-screen bg-gray-100 px-6 py-14 text-gray-900 sm:px-12">
    <div className="mx-auto max-w-3xl">
      <div className="text-center">
        <p className="text-sm font-bold uppercase tracking-wider text-yellow-600">Your impact</p>
        <h1 className="mt-2 text-4xl font-black">Your bananas are traveling.</h1>
        <p className="mx-auto mt-3 max-w-xl text-gray-600">
          Come back to see where they landed, how people reacted, and what grew from what you started.
        </p>
      </div>

      {loading && <div className="mt-12"><LoadingSpinner label="Finding your bananas..." size="md" /></div>}
      {failed && <div className="mt-12 rounded-xl bg-white p-6 text-center shadow-sm">
        We couldn't load your journeys. Please refresh and try again.
      </div>}
      {!loading && !failed && journeys.length === 0 && <div className="mt-12 rounded-2xl bg-white p-8 text-center shadow-sm">
        <div className="text-4xl">🍌</div>
        <h2 className="mt-3 text-xl font-bold">Your first journey is waiting.</h2>
        <p className="mt-2 text-gray-600">Send a banana and its impact will appear here on this device.</p>
        <Link to="/tuttobanana" className="mt-5 inline-block rounded-full bg-yellow-400 px-6 py-3 font-bold hover:bg-yellow-300">
          Send a banana
        </Link>
      </div>}

      <div className="mt-10 space-y-5">
        {journeys.map((journey) => {
          const bananaTitle = BananaType.getBananaTypeByKey(journey.type)?.title || "Tuttobanana";
          const reactions = reactionSummary(journey);
          return <article key={journey.id} className="rounded-2xl border border-yellow-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-yellow-700">{bananaTitle}</p>
                <h2 className="mt-1 text-2xl font-black">Banana #{journey.serialNumber?.toLocaleString("en-US") || "—"}</h2>
                <p className="mt-1 truncate text-sm text-gray-500">Last seen near {journey.currentLocation.label}</p>
              </div>
              <Link to={`/view-banana/id/${journey.id}`} className="shrink-0 rounded-full bg-gray-900 px-4 py-2 text-sm font-bold text-white">
                View
              </Link>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                [journey.stopCount, "stops"],
                [journey.travelMiles.toLocaleString("en-US"), "miles"],
                [journey.descendantCount, "descendants"],
                [journey.reactionCount, "reactions"],
              ].map(([value, label]) => <div key={String(label)} className="rounded-xl bg-gray-50 p-3 text-center">
                <div className="text-xl font-black">{value}</div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
              </div>)}
            </div>

            <div className="mt-4 flex min-h-7 items-center gap-3 text-sm font-semibold text-gray-600">
              {reactions.length > 0 ? reactions.map(([emoji, count]) => <span key={String(emoji)}>{emoji} {count}</span>) :
                <span>No reactions yet—the journey has only just begun.</span>}
            </div>
          </article>;
        })}
      </div>
    </div>
  </main>;
};

export default YourBananasPage;

